import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { tokenHealthClient } from "src/services/requestClient";

const TokenAnalysisContext = React.createContext({});

const TokenAnalysisProvider = ({ children }) => {
  const [stats, setStats] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [topTokens, setTopTokens] = useState(null);
  const [numberOfTokens, setNumberOfTokens] = useState(0);
  const [categories, setCategories] = useState(null);
  const [tokenDescription, setTokenDescription] = useState({});
  const [historyCache, setHistoryCache] = useState({});

  const [top5PriceStable, setTop5PriceStable] = useState([]);
  const [top5AvgIncreasingScore, setTop5AvgIncreasingScore] = useState([]);
  const [top5PriceIncrease, setTop5PriceIncrease] = useState([]);
  const [top5PriceDecrease, setTop5PriceDecrease] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const _fetchTokenHealth = async () => {
    const [stats, tokens, categories] = await Promise.all([
      tokenHealthClient.get("/v1/tokens/statistics"),
      tokenHealthClient.get("/v1/tokens/overview"),
      tokenHealthClient.get("/v1/tokens/categories"),
    ]);
    return {
      stats: stats,
      tokens: tokens.map((element, index) => {
        return { ...element, ...{ rank: index + 1 } };
      }),
      categories: categories,
    };
  };

  const _fetchTop5 = async () => {
    const topTokens = await tokenHealthClient.get("/v1/tokens/top-tokens");
    return topTokens;
  };
  const _setTop5 = () => {
    setTop5PriceStable(
      topTokens?.top_price_stable
        ?.slice((token) => {
          const categories = token["categories"];
          return !categories.includes("Stablecoins") && !categories.includes("USD Stablecoin");
        })
        .slice(0, 5)
        .map((element) => {
          return {
            token_id: element["token_id"],
            name: element["name"],
            symbol: element["symbol"],
            categories: element["categories"],
            figure: element["price_stability"],
          };
        })
    );
    setTop5AvgIncreasingScore(
      topTokens?.top_avg_increasing_score?.slice(0, 5).map((element) => {
        return {
          token_id: element["token_id"],
          name: element["name"],
          symbol: element["symbol"],
          categories: element["categories"],
          figure: element["average_increasing_score"],
        };
      })
    );

    setTop5PriceIncrease(
      topTokens?.top_price_increase?.slice(0, 5).map((element) => {
        return {
          token_id: element["token_id"],
          name: element["name"],
          symbol: element["symbol"],
          categories: element["categories"],
          figure: element["price_changed"],
        };
      })
    );

    setTop5PriceDecrease(
      topTokens?.top_price_decrease?.slice(0, 5).map((element) => {
        return {
          token_id: element["token_id"],
          name: element["name"],
          symbol: element["symbol"],
          categories: element["categories"],
          figure: element["price_changed"],
        };
      })
    );
  };

  const getDescriptionToken = useCallback(
    async (tokenId) => {
      if (!tokenDescription[tokenId]) {
        try {
          const data = await tokenHealthClient.get(`/v1/description/${tokenId}`);
          const tokenDetailData = data;
          let _tokenTemp = tokenDescription;
          _tokenTemp[tokenId] = tokenDetailData;
          setTokenDescription(_tokenTemp);
        } catch (error) {
          console.error("token: ", error);
        }
      }
      return tokenDescription[tokenId];
    },
    [tokens]
  );

  const getTokenHistory = useCallback(
    async (tokenId) => {
      if (!historyCache[tokenId]) {
        const _token = tokens?.find((element) => element["token_id"] === tokenId);
        if (_token) {
          const data = await tokenHealthClient.get(`/v1/tokens/${tokenId}/history`);
          const tokenHistory = data;
          let _result = { token_id: tokenId };
          for (var _history in tokenHistory) {
            _result[_history] = tokenHistory[_history].map((element) => [element[0] * 1000, element[1]]);
          }
          const _historyTemp = historyCache;
          _historyTemp[tokenId] = _result;
          setHistoryCache(_historyTemp);
        }
      }
      return historyCache[tokenId];
    },
    [tokens]
  );

  const fetchData = useCallback(async (withLoading) => {
    withLoading && setLoading(true);
    try {
      let apiData = await _fetchTokenHealth();
      let apiTopTokensData = await _fetchTop5();
      setTopTokens(apiTopTokensData);
      setStats(apiData.stats);
      setTokens(apiData.tokens);
      setCategories(apiData.categories);
    } catch (error) {
      console.debug("token analysis error", error);
      setError(error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData(true);
  }, []);

  useEffect(() => {
    if (tokens) {
      setNumberOfTokens(tokens.length);
    }
  }, [tokens]);

  useEffect(() => {
    if (topTokens) {
      _setTop5();
    }
  }, [topTokens]);

  const contextData = useMemo(() => {
    return {
      loading,
      error,
      ...{
        data: {
          stats,
          tokens,
          categories,
          numberOfTokens,
          ...{
            topAnalytic: {
              top5PriceStable,
              top5AvgIncreasingScore,
              top5PriceDecrease,
              top5PriceIncrease,
            },
          },
        },
        refresh: fetchData,
        getDescriptionToken: getDescriptionToken,
        getTokenHistory: getTokenHistory,
      },
    };
  }, [loading, error, stats, tokens, categories, fetchData]);

  return <TokenAnalysisContext.Provider value={contextData}>{children}</TokenAnalysisContext.Provider>;
};

TokenAnalysisProvider.propTypes = {
  children: PropTypes.node,
};

export default TokenAnalysisProvider;

export const useTokenAnalysisContext = () => React.useContext(TokenAnalysisContext);

export const useTokenAnalysisSelector = (selectorFn) => {
  const { data } = useTokenAnalysisContext();

  return useMemo(() => selectorFn(data), [data]);
};
